import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  TableCellsIcon,
  DocumentIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

// Google Sheets icon component
const GoogleSheetsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.385 2H4.615C3.17 2 2 3.17 2 4.615v14.77C2 20.83 3.17 22 4.615 22h14.77c1.445 0 2.615-1.17 2.615-2.615V4.615C22 3.17 20.83 2 19.385 2zM8 18H6v-2h2v2zm0-4H6v-2h2v2zm0-4H6V8h2v2zm10 8h-8v-2h8v2zm0-4h-8v-2h8v2zm0-4h-8V8h8v2z"/>
  </svg>
);

export default function GoogleSheetsSource({ companyId, onSuccess, onBack }) {
  const [searchParams] = useSearchParams();
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tokens, setTokens] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  // Data state
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [columns, setColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [previewData, setPreviewData] = useState([]);
  
  // Form state
  const [name, setName] = useState('');
  const [headerRow, setHeaderRow] = useState(1);
  
  // UI state
  const [step, setStep] = useState(1); // 1: auth, 2: select spreadsheet, 3: select sheet, 4: configure
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // Check for OAuth callback on mount
  useEffect(() => {
    const googleAuth = searchParams.get('googleAuth');
    const tokenData = searchParams.get('tokenData');
    const error = searchParams.get('error');
    
    if (error) {
      toast.error(`Google authentication failed: ${error}`);
      return;
    }
    
    if (googleAuth === 'success' && tokenData) {
      try {
        const decoded = JSON.parse(atob(tokenData));
        setTokens(decoded);
        setIsAuthenticated(true);
        setStep(2);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
        toast.success('Google account connected successfully!');
        // Load spreadsheets
        loadSpreadsheets(decoded);
      } catch (e) {
        console.error('Failed to decode token data:', e);
        toast.error('Failed to process authentication');
      }
    }
  }, [searchParams]);
  
  const handleGoogleAuth = async () => {
    setIsAuthenticating(true);
    
    try {
      const result = await api.post('/data-sources/google/auth', {
        companyId,
        returnUrl: window.location.href
      });
      
      // Redirect to Google OAuth
      window.location.href = result.data.authUrl;
    } catch (error) {
      console.error('Google auth error:', error);
      toast.error(error.response?.data?.error || 'Failed to start Google authentication');
      setIsAuthenticating(false);
    }
  };
  
  const loadSpreadsheets = async (tokenData = tokens) => {
    if (!tokenData) return;
    
    setIsLoading(true);
    
    try {
      const result = await api.post('/data-sources/google/spreadsheets', {
        accessToken: tokenData.accessToken,
        refreshToken: tokenData.refreshToken
      });
      
      setSpreadsheets(result.data.spreadsheets || []);
    } catch (error) {
      console.error('Load spreadsheets error:', error);
      toast.error('Failed to load spreadsheets');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSelectSpreadsheet = async (spreadsheet) => {
    setSelectedSpreadsheet(spreadsheet);
    setIsLoading(true);
    
    try {
      const result = await api.post('/data-sources/google/spreadsheet-info', {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        spreadsheetId: spreadsheet.id
      });
      
      setSheets(result.data.spreadsheet.sheets || []);
      setName(spreadsheet.name); // Default name to spreadsheet name
      setStep(3);
    } catch (error) {
      console.error('Get spreadsheet info error:', error);
      toast.error('Failed to load spreadsheet details');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSelectSheet = async (sheet) => {
    setSelectedSheet(sheet);
    setIsLoading(true);
    
    try {
      const result = await api.post('/data-sources/google/preview-sheet', {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        spreadsheetId: selectedSpreadsheet.id,
        sheetName: sheet.title,
        headerRow
      });
      
      setColumns(result.data.columns || []);
      setSelectedColumns(new Set(result.data.columns?.map(c => c.name) || []));
      setPreviewData(result.data.preview || []);
      setStep(4);
    } catch (error) {
      console.error('Preview sheet error:', error);
      toast.error('Failed to preview sheet');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleColumnToggle = (columnName) => {
    const updated = new Set(selectedColumns);
    if (updated.has(columnName)) {
      updated.delete(columnName);
    } else {
      updated.add(columnName);
    }
    setSelectedColumns(updated);
  };
  
  const handleSelectAllColumns = () => {
    if (selectedColumns.size === columns.length) {
      setSelectedColumns(new Set());
    } else {
      setSelectedColumns(new Set(columns.map(c => c.name)));
    }
  };
  
  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    
    if (selectedColumns.size === 0) {
      toast.error('Please select at least one column');
      return;
    }
    
    setIsCreating(true);
    
    try {
      // Get full column schema for selected columns
      const selectedColumnSchema = columns.filter(c => selectedColumns.has(c.name));
      
      const connectionConfig = {
        spreadsheetId: selectedSpreadsheet.id,
        spreadsheetName: selectedSpreadsheet.name,
        sheetName: selectedSheet.title,
        sheetId: selectedSheet.sheetId,
        headerRow,
        columns: Array.from(selectedColumns),
        googleAccessToken: tokens.accessToken,
        googleRefreshToken: tokens.refreshToken,
        googleTokenExpiry: tokens.expiry
      };
      
      const result = await api.post('/data-sources', {
        name,
        type: 'googlesheets',
        companyId,
        connectionConfig,
        schemaInfo: {
          tables: [{
            name: selectedSheet.title,
            columns: selectedColumnSchema
          }]
        }
      });
      
      onSuccess(result.data.dataSource);
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.error || 'Failed to create data source');
    } finally {
      setIsCreating(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center gap-4">
        {[
          { num: 1, label: 'Connect' },
          { num: 2, label: 'Spreadsheet' },
          { num: 3, label: 'Sheet' },
          { num: 4, label: 'Configure' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
              step >= s.num 
                ? 'bg-emerald-600 text-white' 
                : 'bg-surface-200 dark:bg-surface-700 text-surface-500'
            }`}>
              {step > s.num ? <CheckCircleIcon className="w-5 h-5" /> : s.num}
            </div>
            <span className={`text-sm ${step >= s.num ? 'text-surface-900 dark:text-white' : 'text-surface-500'}`}>
              {s.label}
            </span>
            {i < 3 && <div className="w-12 h-px bg-surface-200 dark:bg-surface-700" />}
          </div>
        ))}
      </div>
      
      {/* Step 1: Google Authentication */}
      {step === 1 && (
        <div className="card p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <GoogleSheetsIcon className="w-8 h-8 text-emerald-500" />
            <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
              Connect to Google Sheets
            </h3>
          </div>
          
          <div className="text-center py-8">
            <GoogleSheetsIcon className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
              Sign in with Google
            </h4>
            <p className="text-surface-600 dark:text-surface-400 mb-6 max-w-md mx-auto">
              Connect your Google account to access your spreadsheets. We'll only request read-only access to your Google Sheets and Drive.
            </p>
            
            <button
              onClick={handleGoogleAuth}
              disabled={isAuthenticating}
              className="inline-flex items-center gap-3 px-6 py-3 bg-white dark:bg-surface-800 border border-surface-300 dark:border-surface-600 rounded-lg shadow-sm hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors"
            >
              {isAuthenticating ? (
                <ArrowPathIcon className="w-5 h-5 animate-spin text-surface-600" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span className="font-medium text-surface-900 dark:text-white">
                {isAuthenticating ? 'Connecting...' : 'Sign in with Google'}
              </span>
            </button>
          </div>
          
          <div className="flex justify-between pt-4 border-t border-surface-200 dark:border-surface-700">
            <button onClick={onBack} className="btn-secondary">
              Back
            </button>
          </div>
        </div>
      )}
      
      {/* Step 2: Select Spreadsheet */}
      {step === 2 && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
              Select Spreadsheet
            </h3>
            <button
              onClick={() => loadSpreadsheets()}
              disabled={isLoading}
              className="btn-secondary text-sm"
            >
              {isLoading ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : 'Refresh'}
            </button>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <ArrowPathIcon className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : spreadsheets.length === 0 ? (
            <div className="text-center py-8 text-surface-500">
              No spreadsheets found in your Google Drive
            </div>
          ) : (
            <div className="grid gap-2 max-h-96 overflow-y-auto">
              {spreadsheets.map((sheet) => (
                <button
                  key={sheet.id}
                  onClick={() => handleSelectSpreadsheet(sheet)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-surface-200 dark:border-surface-700 hover:border-emerald-300 text-left transition-colors"
                >
                  <GoogleSheetsIcon className="w-8 h-8 text-emerald-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-surface-900 dark:text-white truncate">
                      {sheet.name}
                    </p>
                    <p className="text-sm text-surface-500">
                      Modified {new Date(sheet.modifiedTime).toLocaleDateString()}
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-surface-400" />
                </button>
              ))}
            </div>
          )}
          
          <div className="flex justify-between pt-4 mt-4 border-t border-surface-200 dark:border-surface-700">
            <button onClick={() => setStep(1)} className="btn-secondary">
              Back
            </button>
          </div>
        </div>
      )}
      
      {/* Step 3: Select Sheet */}
      {step === 3 && (
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <GoogleSheetsIcon className="w-6 h-6 text-emerald-500" />
            <div>
              <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
                Select Sheet
              </h3>
              <p className="text-sm text-surface-500">
                {selectedSpreadsheet?.name}
              </p>
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <ArrowPathIcon className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : sheets.length === 0 ? (
            <div className="text-center py-8 text-surface-500">
              No sheets found in this spreadsheet
            </div>
          ) : (
            <div className="grid gap-2">
              {sheets.map((sheet) => (
                <button
                  key={sheet.sheetId}
                  onClick={() => handleSelectSheet(sheet)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-surface-200 dark:border-surface-700 hover:border-emerald-300 text-left transition-colors"
                >
                  <TableCellsIcon className="w-5 h-5 text-surface-400" />
                  <div className="flex-1">
                    <p className="font-medium text-surface-900 dark:text-white">
                      {sheet.title}
                    </p>
                    {sheet.rowCount && (
                      <p className="text-sm text-surface-500">
                        ~{sheet.rowCount.toLocaleString()} rows, {sheet.columnCount} columns
                      </p>
                    )}
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-surface-400" />
                </button>
              ))}
            </div>
          )}
          
          <div className="flex justify-between pt-4 mt-4 border-t border-surface-200 dark:border-surface-700">
            <button onClick={() => setStep(2)} className="btn-secondary">
              Back
            </button>
          </div>
        </div>
      )}
      
      {/* Step 4: Configure & Preview */}
      {step === 4 && (
        <div className="space-y-6">
          {/* Name input */}
          <div className="card p-4">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              Data Source Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder={`${selectedSpreadsheet?.name} - ${selectedSheet?.title}`}
            />
          </div>
          
          {/* Column Selection */}
          {columns.length > 0 && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Select Columns ({selectedColumns.size} of {columns.length})
                </h4>
                <button
                  onClick={handleSelectAllColumns}
                  className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                  {selectedColumns.size === columns.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                {columns.map((col) => (
                  <label
                    key={col.name}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedColumns.has(col.name)
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                        : 'bg-surface-50 dark:bg-surface-800 border border-transparent hover:bg-surface-100 dark:hover:bg-surface-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedColumns.has(col.name)}
                      onChange={() => handleColumnToggle(col.name)}
                      className="rounded text-emerald-600"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-surface-900 dark:text-white truncate" title={col.name}>
                        {col.name}
                      </p>
                      <p className="text-xs text-surface-500">{col.type}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {/* Data Preview */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-surface-900 dark:text-white">
                Data Preview
              </h4>
              <span className="text-sm text-surface-500">
                Showing {Math.min(previewData.length, 10)} rows
              </span>
            </div>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="w-8 h-8 text-emerald-500 animate-spin" />
              </div>
            ) : selectedColumns.size === 0 ? (
              <div className="text-center py-8 text-surface-500">
                Please select at least one column to preview
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 dark:bg-surface-800">
                    <tr>
                      {columns.filter(c => selectedColumns.has(c.name)).map((col, i) => (
                        <th key={i} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400 whitespace-nowrap">
                          {col.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t border-surface-100 dark:border-surface-800">
                        {columns.filter(c => selectedColumns.has(c.name)).map((col, j) => (
                          <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap max-w-xs truncate" title={String(row[col.name] ?? '')}>
                            {String(row[col.name] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex justify-between">
            <button onClick={() => setStep(3)} className="btn-secondary">
              Back
            </button>
            <button
              onClick={handleCreate}
              disabled={isCreating || !name.trim() || selectedColumns.size === 0}
              className="btn-primary"
            >
              {isCreating ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  Create Data Source
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

